import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { updateCompany, getCompany } from "@/services/companyManagement";
import { getUsers } from "@/services/getUsers";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { CompanyTableData } from "./companies-table-columns";

const editCompanySchema = z.object({
  name: z.string().min(1, "Company name is required"),
  companyAdmin: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
});

type EditCompanyFormData = z.infer<typeof editCompanySchema>;

interface EditCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyTableData;
  onSuccess: () => void;
}

export function EditCompanyDialog({
  open,
  onOpenChange,
  company,
  onSuccess,
}: EditCompanyDialogProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);

  // Reset form key when dialog opens
  useEffect(() => {
    if (open) {
      setFormKey(0);
    }
  }, [open]);

  const form = useForm<EditCompanyFormData>({
    resolver: zodResolver(editCompanySchema),
    defaultValues: {
      name: company.name || "",
      companyAdmin: "none",
      address: "",
      phone: "",
    },
  });

  // Fetch company details
  const { data: companyData } = useQuery({
    queryKey: ["company", company.id],
    queryFn: () => getCompany(company.id),
    enabled: open,
  });

  // Fetch available company admins
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const data = await getUsers({
        name: "",
        pageLimit: 100,
        currentPage: 1,
        sort: "createdAt",
        sortOrder: "desc"
      });
      return data.data.filter((user: any) => 
        user.role === "company-admin" && 
        (!user.company || user.company === company.id)
      );
    },
    enabled: open,
  });

  // Update form when company data is loaded
  useEffect(() => {
    if (companyData && usersData) {
      form.reset({
        name: companyData.name,
        companyAdmin: companyData.companyAdmin?._id || "none",
        address: companyData.address || "",
        phone: companyData.phone || "",
      });
      setFormKey(prev => prev + 1); // Force re-render
    }
  }, [companyData, usersData, form]);

  const updateCompanyMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditCompanyFormData }) =>
      updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update company");
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = async (data: EditCompanyFormData) => {
    setIsSubmitting(true);
    try {
      await updateCompanyMutation.mutateAsync({
        id: company.id,
        data: {
          name: data.name,
          companyAdmin: data.companyAdmin === "none" ? undefined : data.companyAdmin || undefined,
          address: data.address || undefined,
          phone: data.phone || undefined,
        },
      });
    } catch (error) {
      // Error is handled in mutation onError
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isSubmitting) {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Company</DialogTitle>
          <DialogDescription>
            Update company information and assign a company admin.
          </DialogDescription>
        </DialogHeader>
        <Form {...form} key={formKey}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter company name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyAdmin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Admin</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a company admin" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No Admin</SelectItem>
                      {usersData?.map((user: any) => (
                        <SelectItem key={user._id} value={user._id}>
                          {user.name || user.email} {user.name && user.email && `(${user.email})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter company address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update Company"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
